import { lazy, Suspense } from "react";
import { createMatchKeyRow } from "./warehouseSettings";
import { Button } from "../../elements/Button";
import { Loading } from "../../elements/Loading";
import { PlusIcon } from "../../icons";
import { useEntityPage } from "../useEntityPage";
import type { MatchKeyRowValue } from "./MatchKeyRow";
import { matchKeyCellErrors } from "./warehouseSettingsRecord";

// Lazy so the drag and drop library isn't loaded behind every record page.
const MatchKeys = lazy(() => import("./MatchKeys"));

function useMatchKeyRows() {
  const { record, onFieldChange, canEdit, fieldErrors } = useEntityPage();
  const rows = (record?.MatchKeys as MatchKeyRowValue[] | undefined) ?? [];
  const setRows = (next: MatchKeyRowValue[]) => onFieldChange({ MatchKeys: next });

  return { rows, setRows, canEdit, fieldErrors };
}

/** The match keys table as a field: it edits the record's rows like any other value. */
export function MatchKeysField() {
  const { rows, setRows, fieldErrors } = useMatchKeyRows();
  // The page reports the field only once a save has been tried, and keeps it current.
  const errors = fieldErrors.MatchKeys ? matchKeyCellErrors(rows) : undefined;

  return (
    <Suspense fallback={<Loading />}>
      <MatchKeys rows={rows} onChange={setRows} errors={errors} />
    </Suspense>
  );
}

/** Beside the section title, styled as a related table's Add. */
export function AddMatchKeyButton() {
  const { rows, setRows, canEdit } = useMatchKeyRows();

  return (
    <Button
      variant="none"
      className="border rounded-2 bg-white text-dark fw-medium hover-bg-light d-flex align-items-center"
      icon={PlusIcon}
      text="Add"
      disabled={!canEdit}
      onClick={() => setRows([...rows, createMatchKeyRow(rows)])}
    />
  );
}

export default MatchKeysField;
